import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";

// Public, no-login catalog the bot links to (wkbeast.org/catalog). Anyone can
// open it; each machine has an "Order on WhatsApp" button that opens a chat with
// the bot pre-filled, so a click turns straight into a sales conversation.
const fmt = (n) => Number(n || 0).toLocaleString();

// Group machines by the coin they mine. Uses the admin's explicit category when
// set, otherwise reads it off the name + notes so existing machines group with
// no extra data entry.
const deriveCategory = (p) => {
  if (p.category && p.category.trim()) return p.category.trim();
  const t = `${p.name || ""} ${p.notes || ""}`.toLowerCase();
  if (/\bbtc\b|bitcoin/.test(t)) return "Bitcoin (BTC)";
  if (/zcash|\bzec\b/.test(t)) return "Zcash (ZEC)";
  if (/kaspa|\bkas\b/.test(t)) return "Kaspa (KAS)";
  if (/doge|litecoin|\bltc\b|scrypt|bells/.test(t)) return "Dogecoin / Litecoin";
  return "Other machines";
};

// Keep groups in the order their first machine appears (which follows the
// admin's manual sort), so a coin they pushed to the top leads the page.
const groupProducts = (products, catOrder) => {
  const order = [];
  const map = {};
  products.forEach((p) => {
    const g = deriveCategory(p);
    if (!map[g]) { map[g] = []; order.push(g); }
    map[g].push(p);
  });
  const groups = order.map((g) => ({ title: g, items: map[g] }));
  // If the admin defined a category order, honour it; unknown groups go last.
  if (catOrder && catOrder.length) {
    groups.sort((a, b) => {
      const ia = catOrder.indexOf(a.title), ib = catOrder.indexOf(b.title);
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
    });
  }
  return groups;
};

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [whatsapp, setWhatsapp] = useState("");
  const [catOrder, setCatOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/catalog`)
      .then((r) => { setProducts(r.data?.products || []); setWhatsapp((r.data?.whatsapp || "").replace(/\D/g, "")); setCatOrder(r.data?.categories || []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const orderLink = (name) => {
    const text = encodeURIComponent(`Hi WKBeast 🐺 — I'm interested in the ${name}. Can you tell me more?`);
    return whatsapp ? `https://wa.me/${whatsapp}?text=${text}` : `https://wa.me/?text=${text}`;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#050505", color: "#fff",
                  fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}>
      {/* Hero */}
      <div style={{ textAlign: "center", padding: "48px 20px 28px", borderBottom: "1px solid #18181B",
                    background: "radial-gradient(600px 200px at 50% -40px, rgba(0,224,84,0.15), transparent)" }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: "#00E054", color: "#000",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: 28, marginBottom: 14 }}>W</div>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800 }}>WKBeast Mining</h1>
        <p style={{ margin: "8px 0 0", color: "#9CA3AF", fontSize: 15 }}>
          Buy a machine, we host and run it for you in the UAE. Pick one below and order in one tap. 🐺
        </p>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 16px 60px" }}>
        {loading && <p style={{ textAlign: "center", color: "#6B7280" }}>Loading our lineup…</p>}
        {error && <p style={{ textAlign: "center", color: "#EF4444" }}>Couldn't load the catalog right now — please try again shortly.</p>}
        {!loading && !error && products.length === 0 && (
          <p style={{ textAlign: "center", color: "#6B7280" }}>New machines coming soon — message us to be first in line.</p>
        )}

        {groupProducts(products, catOrder).map((group) => (
          <div key={group.title} style={{ marginBottom: 34 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px" }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#00E054" }}>{group.title}</h2>
              <span style={{ color: "#4B5563", fontSize: 13 }}>{group.items.length} machine{group.items.length > 1 ? "s" : ""}</span>
              <div style={{ flex: 1, height: 1, background: "#18181B" }} />
            </div>
            <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
              {group.items.map((p, i) => (
            <div key={i} style={{ background: "#0C0C0D", border: "1px solid #1F1F23", borderRadius: 18,
                                  padding: 20, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>{p.name}</h2>
                {p.price ? (
                  <span style={{ whiteSpace: "nowrap", background: "rgba(0,224,84,0.12)", color: "#00E054",
                                 border: "1px solid rgba(0,224,84,0.3)", borderRadius: 10,
                                 padding: "4px 10px", fontWeight: 800 }}>${fmt(p.price)}</span>
                ) : null}
              </div>

              <div style={{ marginTop: 14, display: "grid", gap: 8, fontSize: 14, color: "#D1D5DB" }}>
                {p.monthly_profit ? (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#9CA3AF" }}>💰 Est. profit</span>
                    <b style={{ color: "#00E054" }}>{p.monthly_profit}</b>
                  </div>) : null}
                {p.hosting_fee ? (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#9CA3AF" }}>🏠 Hosting</span><span>${fmt(p.hosting_fee)}/month</span>
                  </div>) : null}
                {p.power ? (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#9CA3AF" }}>⚡ Power</span><span>{p.power}</span>
                  </div>) : null}
                {p.warranty ? (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#9CA3AF" }}>🛡️ Warranty</span><span>{p.warranty}</span>
                  </div>) : null}
              </div>

              {p.notes ? (
                <p style={{ marginTop: 12, fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>{p.notes}</p>
              ) : null}

              <a href={orderLink(p.name)} target="_blank" rel="noopener noreferrer"
                 style={{ marginTop: 18, textAlign: "center", background: "#25D366", color: "#000",
                          fontWeight: 800, textDecoration: "none", borderRadius: 12, padding: "11px 14px",
                          display: "block" }}>
                Order on WhatsApp
              </a>
            </div>
              ))}
            </div>
          </div>
        ))}

        <p style={{ textAlign: "center", color: "#4B5563", fontSize: 12, marginTop: 40 }}>
          WKBeast Mining · hosted in the UAE · wkbeast.org
        </p>
      </div>
    </div>
  );
};

export default Catalog;
